import { Component, OnInit, OnDestroy, inject, isDevMode } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, takeUntil, take } from 'rxjs/operators';
import { Subject, timer } from 'rxjs';
import { environment } from '../environments/environment';
import { AuthService } from './core/auth/auth.service';
import { B2bInvitationDialogService } from './core/services/b2b-invitation-dialog.service';
import { B2bInvitationData } from './standalone-components/dialogs/b2b-invitation-dialog/b2b-invitation-dialog.component';
import { ChannelsService } from './core/api/channels.service';
import { NotificationTemplate } from './core/model/notificationTemplate';
import { ClarityService } from './core/services/clarity.service';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'connect20';

  private destroy$ = new Subject<void>();
  private b2bInvitationService = inject(B2bInvitationDialogService);
  private channelsService = inject(ChannelsService);
  private authService = inject(AuthService);
  private clarityService = inject(ClarityService);

  constructor(router: Router, auth: AuthService) {
    const tag = document.createElement('script');
    tag.async = true;
    tag.src = `https://www.googletagmanager.com/gtag/js?id=${environment.googleAnalyticsId}`;
    document.head.appendChild(tag);

  const inline = document.createElement('script');
  inline.text = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${environment.googleAnalyticsId}', {
        send_page_view: false,
        user_properties: {
          company: '${auth.companyProfile?.id}',
          user: '${auth.userProfile?.info?.sub}',
          userprofile: '${auth.appUserProfile?.profile}'
        }
      });
    `;
    document.head.appendChild(inline);

    router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e: NavigationEnd) => {
        (window as any).gtag('event', 'page_view', {
          page_path: e.urlAfterRedirects,
        });
      });
  }

  ngOnInit(): void {
    this.clarityService.initialize(environment.clarityProjectId);

    // Inicializar la carga de invitaciones B2B despuゼs de la autenticaciИn
    this.initB2bInvitations();
  }

  /**
   * Inicializa la lИgica de invitaciones B2B despuゼs de la autenticaciИn
   *
   * FLUJO DESACOPLADO:
   * 1. AppComponent: Solo dispara la consulta API despuゼs de autenticaciИn
   * 2. B2bInvitationDialogService: Maneja toda la lИgica del diケlogo y respuestas
   * 3. El servicio puede ser usado desde cualquier componente de la app
   */
  private initB2bInvitations(): void {
    this.authService.isAuthenticated$
      .pipe(
        filter(isAuthenticated => isAuthenticated),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (isDevMode()) console.log('Usuario autenticado, inicializando sistema B2B...');

        // Delay de 5 segundos antes de consultar invitaciones
        timer(5000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            if (isDevMode()) console.log('Consultando invitaciones B2B...');

            this.channelsService.channelsGetMyPendingInvitations()
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (invitationsArray: any[]) => {
                  const validInvitations = this.normalizeInvitations(invitationsArray);

                  this.b2bInvitationService.processInvitations(validInvitations);
                },
                error: (error: any) => {
                  console.error('Error al cargar invitaciones B2B:', error);
                }
              });
          });
      });
  }

  /**
   * Normaliza la respuesta del endpoint de invitaciones de Channels para mantener compatibilidad con el diケlogo B2B.
   */
  private normalizeInvitations(invitationsArray: any[]): B2bInvitationData[] {
    if (!Array.isArray(invitationsArray)) {
      return [];
    }

    return invitationsArray
      .map((inv: any) => {
        const id = inv?.id || inv?.sharedChannelId;
        const template = inv?.template || this.buildTemplateFromChannel(inv);
        const status = inv?.status || 'Pending';

        if (!id || !template) {
          return null;
        }

        return {
          id,
          notificationId: inv?.notificationId || inv?.channelKey || '',
          companyId: inv?.companyId || '',
          providerId: inv?.providerId || '',
          providerCompanyId: inv?.providerCompanyId ?? null,
          status,
          activationDate: inv?.activationDate || inv?.invitedAt || inv?.createdAt || '',
          expirationDate: inv?.expirationDate || '',
          createdAt: inv?.createdAt || inv?.invitedAt || '',
          updatedAt: inv?.updatedAt ?? null,
          notes: inv?.notes ?? inv?.channelDescription ?? null,
          template,
          category: inv?.category || null,
          channelName: inv?.channelName,
          channelDescription: inv?.channelDescription,
          channelImage: inv?.channelImage
        } as B2bInvitationData;
      })
      .filter((inv): inv is B2bInvitationData => !!inv);
  }

  /**
   * Construye un template minimal basado en la info del canal cuando el endpoint no provee uno.
   */
  private buildTemplateFromChannel(invitation: any): NotificationTemplate {
    const title = invitation?.channelName || 'Nueva invitaciИn';
    const subtitle = invitation?.channelDescription || 'Tienes una invitaciИn pendiente.';
    const corporateColor = '#123456';

    return {
      header: {
        title: { es: title },
        subtitle: { es: subtitle },
        corporateColor,
        textColor: '#FFFFFF',
        logoUrl: invitation?.channelImage || null
      },
      body: {
        sectionTitle: { es: 'Canal compartido' },
        paragraphs: subtitle ? [{ text: { es: subtitle } }] : undefined
      }
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
